import { useCallback, useEffect, useState } from 'react';

import { getErrorMessage, getPlayers, Player } from '@/lib/api';

/**
 * Custom hook for player data management
 * Handles fetching and managing player list state
 */
export const usePlayerData = (initialLimit: number = 50, initialOffset: number = 0) => {
  // Player list
  const [players, setPlayers] = useState<Player[]>([]);
  // Loading state
  const [loading, setLoading] = useState(true);
  // Error state
  const [error, setError] = useState<string | null>(null);
  // Total count
  const [total, setTotal] = useState<number>(0);
  // Limit
  const [limit, setLimit] = useState<number>(initialLimit);
  // Offset
  const [offset, setOffset] = useState<number>(initialOffset);

  // Function to fetch player list
  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch player list from API
      const response = await getPlayers(limit, offset);

      setPlayers(response.data || []);

      // Set pagination information
      if (response.meta.pagination) {
        setTotal(response.meta.pagination.total);
      }
    } catch (err) {
      console.error('Failed to fetch players:', err);
      // Convert error message to user-friendly format
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  // Initial data fetch and refetch on filter changes
  useEffect(() => {
    fetchPlayers();
  }, [limit, offset, fetchPlayers]);

  // Page change handler
  const handlePageChange = (newPage: number, rowsPerPage: number) => {
    setOffset(newPage * rowsPerPage);
  };

  // Rows per page change handler
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage);
    setOffset(0); // Return to first page when changing rows per page
  };

  return {
    // State
    players,
    loading,
    error,
    total,
    limit,
    offset,

    // Actions
    setLimit,
    setOffset,
    handlePageChange,
    handleRowsPerPageChange,
    refreshData: fetchPlayers,
  };
};

export default usePlayerData;
